import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, ExternalLink } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { t } from '../translations';
import { useScrollReveal } from '../hooks/useScrollReveal';

// Images des projets
const PROJECT_IMGS = [
  "https://customer-assets.emergentagent.com/job_connected-work/artifacts/z7xoesbs_E2DAE32C-FC28-480A-82F7-2C22D3FC8514.png", // Site Construction
  "https://customer-assets.emergentagent.com/job_connected-work/artifacts/k8aburmo_E088F2AA-6C4A-4E33-978A-8F23A3FB2723.png", // Site Plomberie
  "https://customer-assets.emergentagent.com/job_connected-work/artifacts/66utq2we_E1840B20-EFC3-4B4E-891E-21B6BEEA144B.png", // Site Restaurant
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=80&w=800", // ComptaPro IA
  "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?crop=entropy&cs=srgb&fm=jpg&q=80&w=800", // AssistPro IA
  "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=80&w=800", // DevisPro IA
];

export default function PortfolioPage() {
  const { lang } = useLang();
  const tr = t[lang];
  const pTr = tr.portfolioPage;
  const [activeFilter, setActiveFilter] = useState(pTr.filters[0]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [ref, visible] = useScrollReveal();

  const projects = pTr.projects.map((p, i) => ({ ...p, img: PROJECT_IMGS[i] }));
  const filtered = activeFilter === pTr.filters[0]
    ? projects
    : projects.filter(p => p.category === activeFilter);

  return (
    <main data-testid="portfolio-page">
      {/* Hero */}
      <section
        className="pt-32 pb-16 relative"
        style={{ backgroundColor: '#0D1F1F' }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 40% 60%, rgba(155,89,245,0.4) 0%, transparent 50%)' }}
        />
        <div className="section-container relative text-center max-w-3xl mx-auto">
          <h1 className="font-sora font-black text-4xl sm:text-5xl text-white mb-3 animate-fadeInUp">
            {pTr.hero.title}
          </h1>
          <div className="w-14 h-0.5 gradient-bg rounded-full mx-auto mb-6" />
          <p className="text-gray-300 text-base md:text-lg font-inter animate-fadeInUp delay-100">
            {pTr.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-10" style={{ backgroundColor: '#0A1A1A' }}>
        <div className="section-container">
          <div className="flex flex-wrap gap-3 justify-center">
            {pTr.filters.map((f, i) => (
              <button
                key={i}
                data-testid={`filter-${f}`}
                onClick={() => setActiveFilter(f)}
                className={`px-5 py-2 rounded-full text-sm font-medium font-sora transition-all duration-200 ${
                  activeFilter === f
                    ? 'gradient-bg text-white'
                    : 'border border-white/15 text-gray-300 hover:border-exevori-primary hover:text-exevori-primary'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="pb-24" style={{ backgroundColor: '#0A1A1A' }}>
        <div className="section-container">
          <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((proj, i) => (
              <div
                key={i}
                data-testid={`project-card-${i}`}
                className={`exevori-card overflow-hidden group cursor-pointer reveal ${visible ? 'visible' : ''}`}
                style={{ transitionDelay: `${i * 100}ms` }}
                onClick={() => setSelectedProject(proj)}
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={proj.img}
                    alt={proj.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(10,26,26,0.9) 100%)' }} />
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${proj.color}`} />
                  <span
                    className="absolute top-3 right-3 px-2 py-1 rounded-lg text-xs font-medium font-inter"
                    style={{ background: 'rgba(0,0,0,0.6)', color: '#9CA3AF', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    {proj.type}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin size={12} className="text-gray-500" />
                    <span className="text-gray-500 text-xs font-inter">{proj.location}</span>
                  </div>
                  <h3 className="font-sora font-bold text-white text-lg mb-2 group-hover:text-exevori-primary transition-colors">
                    {proj.name}
                  </h3>
                  <p className="text-gray-400 text-sm font-inter leading-relaxed mb-4 line-clamp-2">{proj.desc}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {proj.tags.map((tag, j) => (
                      <span key={j} className="px-2 py-1 text-xs rounded-lg font-inter text-gray-400 border border-white/10">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <button className="text-exevori-primary text-sm font-medium font-inter flex items-center gap-1 hover:gap-2 transition-all">
                    {lang === 'fr' ? 'Voir les détails' : 'View details'} <ExternalLink size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: '#0D1F1F' }}>
        <div className="section-container text-center">
          <h2 className="font-sora font-bold text-2xl md:text-3xl text-white mb-4">
            {lang === 'fr' ? 'Vous avez un projet similaire ?' : 'Have a similar project?'}
          </h2>
          <p className="text-gray-300 font-inter mb-8">
            {lang === 'fr'
              ? 'Discutons de votre idée et voyons comment nous pouvons la concrétiser.'
              : 'Let\'s discuss your idea and see how we can bring it to life.'}
          </p>
          <Link to="/devis" data-testid="portfolio-cta" className="btn-primary">
            {tr.nav.quote} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Project Modal - Professional Design */}
      {selectedProject && (
        <div
          data-testid="project-modal"
          className="fixed inset-0 z-50 overflow-y-auto"
          style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(12px)' }}
          onClick={() => setSelectedProject(null)}
        >
          {/* Close button - always visible */}
          <button
            data-testid="close-modal"
            onClick={() => setSelectedProject(null)}
            className="fixed top-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center bg-white/10 text-white hover:bg-white/20 transition-all text-2xl font-light border border-white/20"
          >
            ×
          </button>

          <div className="min-h-screen flex flex-col lg:flex-row">
            {/* Left side - Image showcase */}
            <div 
              className="lg:w-3/5 p-6 lg:p-12 flex items-center justify-center"
              onClick={e => e.stopPropagation()}
            >
              {selectedProject.img ? (
                <div className="relative w-full max-w-4xl">
                  {/* Browser mockup frame */}
                  <div className="rounded-xl overflow-hidden shadow-2xl border border-white/10" style={{ background: '#1a1a1a' }}>
                    {/* Browser header */}
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10" style={{ background: '#0d0d0d' }}>
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                      </div>
                      <div className="flex-1 mx-4">
                        <div className="bg-white/5 rounded-md px-3 py-1.5 text-xs text-gray-400 font-mono">
                          www.exemple-client.com
                        </div>
                      </div>
                    </div>
                    {/* Website screenshot - scrollable */}
                    <div className="overflow-y-auto" style={{ maxHeight: '70vh' }}>
                      <img 
                        src={selectedProject.img} 
                        alt={selectedProject.name}
                        className="w-full h-auto"
                        style={{ imageRendering: 'crisp-edges' }}
                      />
                    </div>
                  </div>
                  {/* Glow effect */}
                  <div 
                    className={`absolute -inset-4 -z-10 opacity-30 blur-3xl bg-gradient-to-r ${selectedProject.color}`}
                  />
                </div>
              ) : (
                <div 
                  className={`w-full max-w-md aspect-video rounded-2xl bg-gradient-to-br ${selectedProject.color} flex items-center justify-center`}
                >
                  <span className="text-white/50 text-6xl font-sora font-bold">
                    {selectedProject.name.charAt(0)}
                  </span>
                </div>
              )}
            </div>

            {/* Right side - Project details */}
            <div 
              className="lg:w-2/5 p-6 lg:p-12 lg:overflow-y-auto flex flex-col justify-center"
              style={{ background: 'rgba(21,46,46,0.95)' }}
              onClick={e => e.stopPropagation()}
            >
              <div className={`w-16 h-1 rounded-full bg-gradient-to-r ${selectedProject.color} mb-6`} />
              
              <span className="text-exevori-primary font-sora font-semibold text-xs uppercase tracking-wider mb-3 block">
                {selectedProject.type}
              </span>
              
              <h2 className="font-sora font-bold text-3xl lg:text-4xl text-white mb-3">
                {selectedProject.name}
              </h2>
              
              <p className="text-gray-400 text-sm mb-8 font-inter">
                {selectedProject.client}
              </p>

              <div className="space-y-6 mb-8">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 font-inter">
                    {lang === 'fr' ? 'Objectif' : 'Objective'}
                  </div>
                  <p className="text-gray-300 font-inter">{selectedProject.objective}</p>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 font-inter">
                    {lang === 'fr' ? 'Résultat' : 'Result'}
                  </div>
                  <p className="text-white font-inter font-medium">{selectedProject.result}</p>
                </div>
              </div>

              <p className="text-gray-400 font-inter leading-relaxed mb-8">
                {selectedProject.desc}
              </p>

              <div className="flex flex-wrap gap-2 mb-8">
                {selectedProject.tags.map((tag, i) => (
                  <span 
                    key={i} 
                    className="px-3 py-1.5 text-sm rounded-full font-inter text-white/80 bg-white/5 border border-white/10"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <Link 
                to="/devis" 
                className="btn-primary w-full justify-center text-center" 
                onClick={() => setSelectedProject(null)}
              >
                {lang === 'fr' ? 'Lancer un projet similaire' : 'Start a similar project'} 
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
